import { useCalendarStore } from './useCalendarStore';
import { useTaskEvents } from './useTaskEvents';
import { useTimeEntryEvents } from './useTimeEntryEvents';
import { useExternalCalendarEvents } from './useExternalCalendarEvents';
import { resolveCalendarFilterCategory } from '../types';
import type { CalendarEvent, DateRange } from '../types';

function toDateRange(range: { start: Date; end: Date }): DateRange {
  return {
    start: range.start.toISOString().slice(0, 10),
    end: range.end.toISOString().slice(0, 10),
  };
}

export function useCalendarEvents(enabled = true): {
  events: CalendarEvent[];
  isLoading: boolean;
  isError: boolean;
} {
  const { visibleRange, activeFilters } = useCalendarStore();
  const range = toDateRange(visibleRange);

  const tasks = useTaskEvents(range, enabled);
  const timeEntries = useTimeEntryEvents(range, enabled);
  // externalEvents also carries native TimeOff (`khronox`) and the caller's
  // own internal (`internal`) events — same unified `GET /calendar/events` feed.
  const externalEvents = useExternalCalendarEvents(range, enabled);
  // Personal tasks (future): usePersonalTaskEvents(range, enabled)

  const events = (() => {
    const all = [...tasks.data, ...timeEntries.data, ...externalEvents.data];
    return all.filter((e) => activeFilters.includes(resolveCalendarFilterCategory(e)));
  })();

  // Composite view — best effort: a failing source only surfaces a toast via
  // the global handler; the error state is reserved for when EVERY source
  // fails (disabled legacy sources are idle, so they never trigger it).
  const sources = [tasks, timeEntries, externalEvents];

  return {
    events,
    isLoading: sources.some((s) => s.isLoading),
    isError: sources.every((s) => s.isError),
  };
}
